<template>
    <div class="data-indicator-list-item">
        <!-- Contenu -->
        <div class="data-indicator-list-item__wrapper">
            <h2
                class="data-indicator-list-item__title text-primary font-semibold"
            >
                {{ name }}
            </h2>
            <div
                class="data-indicator-list-item__content max-h-[18rem] overflow-y-auto relative"
                :style="
                    indicatorList && indicatorList?.length > 3
                        ? {
                              maskImage:
                                  'linear-gradient(to bottom, black calc(100% - 20px),transparent 100%)',
                          }
                        : null
                "
            >
                <div
                    v-for="indicator in indicatorList"
                    :key="`${indicator.graph_id}-${indicator.type}`"
                >
                    <div
                        class="data-indicator-list-item__content-item"
                        :data-active="indicator.value != '-'"
                        @click="
                            indicator.value == '-'
                                ? null
                                : $emit('click:kpi', {
                                      type: indicator.type,
                                      graph_id: indicator.graph_id,
                                  })
                        "
                        v-tooltip.right="getItemTooltip(indicator)"
                    >
                        <div class="data-indicator-list-item__content-name">
                            <BaseIcon
                                v-if="indicator.icon"
                                :icon="indicator.icon"
                                :color="indicator.icon_color"
                            />
                            <div class="text-primary">
                                {{ indicator.name }}
                                <p class="text-primary text-sm font-light">
                                    {{ getIndicatorSubtitle(indicator) }}
                                </p>
                            </div>
                        </div>
                        <p
                            class="data-indicator-list-item__content-value text-primary font-semibold whitespace-nowrap"
                        >
                            {{ indicator.value }} {{ indicator.unit }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n"
import { Indicator } from "@types"
type Props = {
    /**
     * Nom du graphique dont les données seront affichées
     * */
    name: string
    /**
     * Liste des indicateur associé au graphique.
     * - `type` Type de l'indicateur.
     * - `name` Nom de l'indicateur.
     * - `value` Valeur de l'indicateur.
     * - `unit` Unité de l'indicateur.
     * - `icon` Icône de l'indicateur.
     * - `icon_color` Couleur de l'icône de l'indicateur.
     * - `subtitle` Sous-titre optionnel ; sinon dérivé de `type` (ex. mean → « Moyenne »).
     * - `tooltipHint` Texte métier optionnel dans l’infobulle (avant « Aller au graphique »).
     */
    indicatorList: Indicator[] | undefined
}
defineProps<Props>()

defineEmits(["click:kpi"])

const { t, te } = useI18n({
    messages: {
        fr: {
            tooltip: "Aller au graphique",
            empty: "Aucune donnée disponible",
            stat: {
                sum: "Somme",
                mean: "Moyenne",
                min: "Minimum",
                max: "Maximum",
                last: "Dernière valeur",
                first: "Première valeur",
            },
        },
    },
})

/*------------------------------------*/
/**             Computed              */
/*----------------------------------- */

/**
 * Infobulle : explication métier optionnelle, puis rappel de navigation vers le graphique.
 */
function getItemTooltip(indicator: Indicator): string {
    if (indicator.value === "-") {
        return t("empty")
    }
    const navigate = t("tooltip")
    const hint = indicator.tooltipHint?.trim()
    if (hint) {
        return `${hint}\n\n${navigate}`
    }
    return navigate
}

/**
 * Sous-titre sous le nom de l’indicateur : texte fourni par la page ou dérivé du `type`.
 */
function getIndicatorSubtitle(indicator: Indicator): string {
    if (indicator.subtitle) {
        return indicator.subtitle
    }
    return getKPITypeFromStat(indicator)
}

/**
 * Libellé générique à partir du suffixe de `type` après le premier « . » (ex. *.mean).
 */
function getKPITypeFromStat(indicator: Indicator): string {
    const index = indicator.type.indexOf(".")
    if (index == -1) return ""
    const indicatorType = indicator.type.slice(index + 1, indicator.type.length)

    return te(`stat.${indicatorType}`)
        ? t(`stat.${indicatorType}`)
        : "Type erroné"
}
</script>

<style>
.data-indicator-list-item {
    display: flex;
    border-radius: 6px;
    gap: 18px;
    padding: 17.5px;

    border-radius: 6px;

    height: fit-content;

    background-color: var(--p-card-background);

    border: 1px solid var(--p-surface-300);
    &:hover {
        border: 1px solid var(--p-blue-500);
    }

    & > .data-indicator-list-item__icon {
        display: flex;
        align-items: center;
    }

    .data-indicator-list-item__wrapper {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }
    .data-indicator-list-item__content-item {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        gap: 4rem;
        border-radius: 6px;
        padding: 1rem;
        max-width: 26rem;
        min-width: 26rem;

        &[data-active="true"] {
            cursor: pointer;
            &:hover {
                background-color: var(--p-blue-50);
            }
        }
    }
    .data-indicator-list-item__content-name {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 1rem;
        p {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
        }
    }
    .data-indicator-list-item__content-value {
        display: flex;
        align-items: center;
    }
}
</style>
